/**
 * Builds and saves the recovery artifact for private (E2EE) mode: a PDF
 * containing a QR code (encoding the recovery key), the human-readable key,
 * restore instructions, and a security warning.
 *
 * On web the PDF is downloaded via an <a download>. On native (Capacitor) that
 * doesn't work in the webview, so the PDF is written to the filesystem and
 * offered through the iOS/Android share sheet (Save to Files, etc.).
 */
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/** Prefix so a scanner can recognise (and validate) a Jilu recovery QR. */
export const RECOVERY_QR_PREFIX = 'jilu-recovery:';

const FILE_NAME = 'jilu-recovery-key.pdf';

export const buildRecoveryQrPayload = (recoveryKey: string): string =>
  `${RECOVERY_QR_PREFIX}${recoveryKey}`;

export const generateRecoveryQrDataUrl = (recoveryKey: string): Promise<string> =>
  QRCode.toDataURL(buildRecoveryQrPayload(recoveryKey), { width: 480, margin: 1 });

const buildRecoveryPdfDoc = async (recoveryKey: string): Promise<jsPDF> => {
  const qrDataUrl = await generateRecoveryQrDataUrl(recoveryKey);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const center = pageWidth / 2;

  doc.setFontSize(20);
  doc.text('Jilu recovery key', center, 72, { align: 'center' });

  doc.setFontSize(11);
  doc.setTextColor(120);
  doc.text(
    'Keep this safe. Anyone with this key or QR code can read your encrypted data.',
    center,
    98,
    { align: 'center', maxWidth: pageWidth - 96 }
  );

  const qrSize = 220;
  doc.addImage(qrDataUrl, 'PNG', center - qrSize / 2, 124, qrSize, qrSize);

  doc.setTextColor(0);
  doc.setFontSize(16);
  doc.text(recoveryKey, center, 380, { align: 'center' });

  doc.setFontSize(11);
  doc.setTextColor(120);
  doc.text(
    [
      'To access your entries on a new device:',
      '1. Sign in with Google or Apple.',
      '2. When asked for your recovery key, scan this QR code or type the key above.',
      '',
      'We cannot reset or recover this key for you. If you lose it, your data cannot be decrypted.',
    ],
    center,
    424,
    { align: 'center', maxWidth: pageWidth - 96 }
  );

  return doc;
};

const downloadOnWeb = (blob: Blob) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = FILE_NAME;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const shareOnNative = async (doc: jsPDF) => {
  // jsPDF data URI → bare base64 for Filesystem.writeFile (writes binary).
  const base64 = doc.output('datauristring').split('base64,')[1];
  const { uri } = await Filesystem.writeFile({
    path: FILE_NAME,
    data: base64,
    directory: Directory.Cache,
  });
  // Use the canonical file:// URI returned by writeFile for the share sheet.
  await Share.share({
    title: 'Jilu recovery key',
    text: 'Your Jilu recovery key — keep it somewhere safe.',
    url: uri,
    files: [uri],
  });
};

/** Save the recovery PDF — download on web, share sheet on native. */
export const saveRecoveryPdf = async (recoveryKey: string): Promise<void> => {
  const doc = await buildRecoveryPdfDoc(recoveryKey);
  if (Capacitor.isNativePlatform()) {
    await shareOnNative(doc);
  } else {
    downloadOnWeb(doc.output('blob'));
  }
};
